import {
  fetchError, fetchRequest, fetchSuccess, fetchVersion, fetchVersionError, fetchVersionSuccess,
  fetchFirmwareError, fetchFirmware, fetchFirmwareSuccess
} from "./actions";
import { fetch, fetchFirmwareData, fetchVersionData} from "../../repo/application";
import { isEmpty } from "ramda";
import { isAuthError } from "../index";
import { put, takeEvery, takeLatest } from "redux-saga/effects";
import { dirty } from "../session/actions";
import { ApplicationActionTypes } from "./types";

// Request tracking to prevent duplicates
const pendingRequests = new Map();

function* handleFetch(action: ReturnType<typeof fetchRequest>) {
  const requestKey = 'applications';
  
  // Check if request is already pending
  if (pendingRequests.has(requestKey)) {
    console.log('WARN', 'Applications request already pending, skipping duplicate');
    return;
  }
  
  try {
    pendingRequests.set(requestKey, true);
    const application = yield fetch();
    
    if (application.errors && !isEmpty(application.errors)) {
      if (isAuthError(application.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(application.errors)));
    } else {
      yield put(fetchSuccess(application));
      action.payload.callback && action.payload.callback(application);
    }
  } catch (err) {
    console.log('err', err)
    yield put(fetchError(err));
  } finally {
    pendingRequests.delete(requestKey);
  }
}

function* handleFetchVersion(action: ReturnType<typeof fetchVersion>) {
  const requestKey = `version_${action.payload.application}`;
  
  // Check if request is already pending
  if (pendingRequests.has(requestKey)) {
    console.log('WARN', `Version request for ${action.payload.application} already pending, skipping duplicate`);
    return;
  }
  
  try {
    pendingRequests.set(requestKey, true);
    const application = yield fetchVersionData(action.payload.application);
    
    if (application.errors && !isEmpty(application.errors)) {
      if (isAuthError(application.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(application.errors)));
    } else {
      yield put(fetchVersionSuccess(application));
    }
  } catch (err) {
    console.log('err', err)
    yield put(fetchVersionError(err));
  } finally {
    pendingRequests.delete(requestKey);
  }
}

function* handleFetchFirmWare(action: ReturnType<typeof fetchFirmware>) {
  const { variant, model } = action.payload.params;
  const requestKey = `firmware_${variant}_${model}`;
  
  // Check if request is already pending
  if (pendingRequests.has(requestKey)) {
    console.log('WARN', `Firmware request for ${variant}/${model} already pending, skipping duplicate`);
    return;
  }
  
  try {
    pendingRequests.set(requestKey, true);
    const firmwares = yield fetchFirmwareData(action.payload.params);
    
    if (firmwares.errors && !isEmpty(firmwares.errors)) {
      if (isAuthError(firmwares.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(firmwares.errors)));
    } else {
      yield put(fetchFirmwareSuccess(model, firmwares));
      action.payload.callback && action.payload.callback(firmwares);
    }
  } catch (err) {
    console.log('err', err)
    yield put(fetchFirmwareError(err));
  } finally {
    pendingRequests.delete(requestKey);
  }
}

function* watchFetchRequest() {
  // Use takeLatest to cancel previous requests when new ones come in
  yield takeEvery(ApplicationActionTypes.FETCH, handleFetch);
  yield takeEvery(ApplicationActionTypes.FETCH_VERSION, handleFetchVersion);
  yield takeEvery(ApplicationActionTypes.FETCH_FIRMWARE, handleFetchFirmWare);
}

export default watchFetchRequest;
